import {
  FloatingFormItem,
  InputDate,
  InputDecimal,
} from '@/components/_common';
import EmployeesInfiniteSelectField from '@/components/_common/EmployeesInfiniteSelectField';
import PortoPolicyCard from '@/components/_common/PortoPolicyCard';
import { usePortoPoliciesList } from '@/core/domain/porto/policies/porto-policies.hooks';
import { PortoPolicy } from '@/core/domain/porto/policies/porto-policies.types';
import { useDiagnosticGenerateWorkOrder } from '@/core/domain/workorder/Diagnostics/Diagnostics.hooks';
import {
  Diagnostic,
  DiagnosticGenerateWorkOrder,
} from '@/core/domain/workorder/Diagnostics/Diagnostics.types';
import { useWorkOrderCreateByPorto } from '@/core/domain/workorder/WorkOrders/WorkOrders.hooks';
import {
  WorkOrderAddPolicyRequest,
  WorkOrderCreateByPolicyCompanyCode,
} from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import {
  WorkOrderComplementTankLevel,
  WorkOrderComplementType,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { useWorkorderConfigurationCompanyQuery } from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.hooks';
import { useDefaultEmptyFieldError } from '@/hooks';
import { isAppPortocar } from '@/index';
import { DateTimeFormat } from '@/utils/moment';
import {
  Checkbox,
  Col,
  Form,
  Input,
  Modal,
  ModalProps,
  Row,
  Select,
  Skeleton,
  Typography,
} from 'antd';
import moment, { Moment } from 'moment';
import { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';

const { Title } = Typography;

interface Props extends Omit<ModalProps, 'onCancel'> {
  diagnostic?: Diagnostic;
  companyId: number;
  onCancel: () => void;
}

export interface GenerateWorkOrderFormValues {
  chargerDiagnostic: boolean;
  estimatorId: number;
  includePart: boolean;
  lastOilChange?: Moment;
  lastOilChangeKilometers?: number;
  currentKilometers?: number;
  prisma?: string;
  tankLevel?: WorkOrderComplementTankLevel;
  policy?: {
    companyCode?: string;
    policyNumber?: string;
    customer?: {
      birthDate?: string;
      documentNumber?: string;
      contacts?: { email: string; phone: string };
      name?: string;
    };
    vehicle?: {
      chassi?: string;
      model?: string;
      brand?: string;
      yearFabrication?: number;
      yearModel?: number;
    };
    benefits: PortoPolicy['benefits'];
  };
}

const DiagnosticGenerateBudgetModal = ({
  open,
  onCancel,
  companyId,
  diagnostic,
}: Props) => {
  const requiredField = useDefaultEmptyFieldError();
  const history = useHistory();
  const intl = useIntl().formatMessage;
  const [form] = Form.useForm<GenerateWorkOrderFormValues>();

  const hasFuelEletric = useMemo(() => {
    return diagnostic?.vehicle?.fuelName?.toLowerCase()?.includes('elétrico');
  }, [diagnostic]);

  const policyNumber = Form.useWatch(['policy', 'policyNumber'], form);

  const generateBudgetMutation = useDiagnosticGenerateWorkOrder();
  const generateBudgetByPolicy = useWorkOrderCreateByPorto();

  const handleCancel = useCallback(() => {
    if (generateBudgetByPolicy.isLoading) return;

    form.resetFields();
    onCancel();
  }, [generateBudgetByPolicy.isLoading]);

  const configBudget = useWorkorderConfigurationCompanyQuery(
    diagnostic?.company.id,
    isAppPortocar && open,
  );

  const policyList = usePortoPoliciesList(
    diagnostic?.vehicle.licensePlate,
    open && isAppPortocar,
  );

  const hasPolicies = useMemo(
    () => (policyList.data?.length || 0) > 0,
    [policyList.data],
  );

  useEffect(() => {
    if (policyList.data?.length) {
      const policy = policyList.data[0];
      const { email, phone, ...restCustomer } = policy.customer;

      form.setFieldsValue({
        policy: {
          companyCode: policy.companyCode,
          policyNumber: policy.policyNumber,
          customer: { ...restCustomer, contacts: { email, phone } },
          vehicle: policy.vehicle,
          benefits: policy.benefits,
        },
      });
    } else {
      form.setFieldValue('policy', undefined);
    }
  }, [policyList.dataUpdatedAt]);

  const handleOk = async () => {
    if (diagnostic) {
      const values = await form.validateFields();

      let response: { complementId: number; workOrderId: number };

      if (isAppPortocar && hasPolicies && values.policy?.policyNumber) {
        const result = await generateBudgetByPolicy.mutateAsync({
          companyCode:
            `domain.enums.company_code.${values.policy.companyCode?.toLowerCase()}` as WorkOrderCreateByPolicyCompanyCode,
          companyId: diagnostic.company.id,
          entryDate: diagnostic.entryDate,
          estimatorId: values.estimatorId,
          workOrderType: WorkOrderComplementType.WorkOrder,
          customer: { ...values.policy.customer },
          customerId: diagnostic.customer.id,
          customerType: diagnostic.customer.personType,
          lastOilChange: String(values.lastOilChange?.format()),
          lastOilChangeKilometers: Number(values.lastOilChangeKilometers),
          policyNumber: String(values.policy.policyNumber),
          vehicleId: diagnostic.vehicle.id,
          vehicle: {
            ...values.policy.vehicle,
            currentKilometers: Number(values.currentKilometers),
            licensePlate: diagnostic.vehicle.licensePlate,
            prisma: values.prisma,
            tankLevel: values.tankLevel,
          } as WorkOrderAddPolicyRequest['vehicle'],
          diagnosticId: diagnostic.id,
          partIds: diagnostic.items.map((item) => item.partId),
          includeParts: values.includePart,
          chargerDiagnostic: values.chargerDiagnostic,
          applyPartnerDiscount: true,
        });

        response = {
          complementId: result.workOrderComplementId,
          workOrderId: result.workOrderId,
        };
      } else {
        const obj: DiagnosticGenerateWorkOrder = {
          ...values,
          diagnosticId: diagnostic.id,
          companyId: diagnostic.company.id,
          vehicleId: diagnostic.vehicle.id,
          entryDate: moment().format(DateTimeFormat),
          partIds: diagnostic.items.map((item) => item.partId),
          includeParts: values.includePart,
        };

        response = await generateBudgetMutation.mutateAsync(obj);
      }

      history.push(
        `/app/movements/workOrders/${response.workOrderId}/complements/${response.complementId}/generalInfo`,
      );

      handleCancel();
    }
  };

  return (
    <Modal
      open={open}
      title={intl({ id: 'diagnostics.modal.generateBudget.title' })}
      width="50%"
      onCancel={handleCancel}
      onOk={handleOk}
      okButtonProps={{
        disabled: policyList.isLoading || (hasPolicies && !policyNumber),
      }}
      cancelButtonProps={{ disabled: generateBudgetByPolicy.isLoading }}
      closable={!generateBudgetByPolicy.isLoading}
      confirmLoading={
        generateBudgetMutation.isLoading || generateBudgetByPolicy.isLoading
      }
      okText={intl({
        id: 'diagnostics.modal.generateBudget.btn.generateBudget',
      })}
    >
      <Form form={form}>
        {isAppPortocar && (
          <>
            <Form.Item
              hidden
              name={['policy', 'companyCode']}
              rules={
                !!policyList.data?.length
                  ? [{ required: true, message: 'Selecione uma apólice' }]
                  : undefined
              }
            />
            <Form.Item hidden name={['policy', 'policyNumber']} />
            <Form.Item hidden name={['policy', 'customer']} />
            <Form.Item hidden name={['policy', 'vehicle']} />
            <Form.Item hidden name={['policy', 'benefits']} />
          </>
        )}

        <Row gutter={[24, 24]}>
          <Col span={12}>
            <FloatingFormItem valuePropName="checked" name="chargerDiagnostic">
              <Checkbox>
                {intl({
                  id: 'diagnostics.modal.generateBudget.checkbox.demand',
                })}
              </Checkbox>
            </FloatingFormItem>
          </Col>

          <Col span={12}>
            <FloatingFormItem valuePropName="checked" name="includePart">
              <Checkbox>
                {intl({
                  id: 'diagnostics.modal.generateBudget.checkbox.include',
                })}
              </Checkbox>
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <FloatingFormItem
              name="estimatorId"
              label={intl({
                id: 'workOrders.domain.complements.estimator',
              })}
              rules={[requiredField]}
            >
              <EmployeesInfiniteSelectField
                queryFilter={{ companyId: companyId, estimator: true }}
              />
            </FloatingFormItem>
          </Col>
        </Row>

        {hasPolicies && isAppPortocar && (
          <>
            <Row gutter={[16, 8]}>
              <Col span={24}>
                <Title level={4}>
                  {intl({
                    id: 'workOrders.domain.vehicle',
                  })}
                </Title>
              </Col>

              <Col span={8}>
                <FloatingFormItem
                  name="prisma"
                  label={intl({
                    id: 'workOrders.domain.complements.prisma',
                  })}
                  rules={[
                    {
                      required: configBudget.data?.prisma,
                      message: intl({
                        id: 'workOrders.fieldRequiredByConfigCompany',
                      }),
                    },
                  ]}
                >
                  <Input />
                </FloatingFormItem>
              </Col>

              <Col span={8}>
                <FloatingFormItem
                  name="currentKilometers"
                  label={intl({
                    id: 'workOrders.domain.complements.currentKilometers',
                  })}
                  rules={[
                    {
                      required:
                        isAppPortocar || configBudget.data?.isKmRequired,
                      message: intl({
                        id: 'workOrders.fieldRequiredByConfigCompany',
                      }),
                    },
                  ]}
                >
                  <InputDecimal fractions={0} min={0} max={5000000} />
                </FloatingFormItem>
              </Col>

              <Col span={8}>
                <FloatingFormItem
                  name="tankLevel"
                  label={intl({
                    id: 'workOrders.domain.complements.tankLevel',
                  })}
                  rules={[
                    {
                      required: configBudget.data?.tankLevel,
                      message: intl({
                        id: 'workOrders.fieldRequiredByConfigCompany',
                      }),
                    },
                  ]}
                >
                  <Select
                    allowClear
                    style={{ width: '100%' }}
                    options={Object.values(WorkOrderComplementTankLevel).map(
                      (tankLevel) => ({
                        label: intl({
                          id: `workOrders.${tankLevel}`,
                        }),
                        value: tankLevel,
                      }),
                    )}
                  />
                </FloatingFormItem>
              </Col>

              <Col span={12}>
                <FloatingFormItem
                  name="lastOilChange"
                  label={intl({
                    id: 'workOrders.domain.complements.lastOilChange',
                  })}
                  rules={hasFuelEletric ? [] : [requiredField]}
                >
                  <InputDate
                    disabledDate={(date) => date.isAfter(moment(), 'day')}
                  />
                </FloatingFormItem>
              </Col>

              <Col span={12}>
                <FloatingFormItem
                  name="lastOilChangeKilometers"
                  label={intl({
                    id: 'workOrders.domain.complements.lastOilChangeKilometers',
                  })}
                  rules={hasFuelEletric ? [] : [requiredField]}
                >
                  <InputDecimal fractions={0} min={0} />
                </FloatingFormItem>
              </Col>
            </Row>
          </>
        )}

        {isAppPortocar && (
          <Row gutter={[16, 8]}>
            {policyList.isLoading && (
              <Col span={24}>
                <Skeleton loading />
              </Col>
            )}

            {hasPolicies && (
              <>
                <Col span={24}>
                  <Title level={4}>Apólices do veículo</Title>

                  <Form.Item shouldUpdate noStyle>
                    {({ getFieldError }) => {
                      const errors = getFieldError(['policy', 'companyCode']);
                      return errors.length ? (
                        <span style={{ color: 'red' }}>{errors[0]}</span>
                      ) : null;
                    }}
                  </Form.Item>

                  {policyList.data?.map((policy) => (
                    <PortoPolicyCard
                      style={{ marginTop: 16 }}
                      key={policy.policyNumber}
                      active={policy.policyNumber === policyNumber}
                      onClick={() => {
                        form.setFields([{ name: 'customerId', errors: [] }]);

                        if (policyNumber === policy.policyNumber) {
                          form.setFieldValue('policy', undefined);
                        } else {
                          const { email, phone, ...restCustomer } =
                            policy.customer;

                          form.setFieldsValue({
                            policy: {
                              companyCode: policy.companyCode,
                              policyNumber: policy.policyNumber,
                              customer: {
                                ...restCustomer,
                                contacts: { email, phone },
                              },
                              vehicle: policy.vehicle,
                              benefits: policy.benefits,
                            },
                          });
                        }
                      }}
                      policy={policy}
                      vehicle={{
                        ...policy.vehicle,
                        licensePlate: diagnostic?.vehicle
                          .licensePlate as string,
                      }}
                    />
                  ))}
                </Col>
              </>
            )}
          </Row>
        )}
      </Form>
    </Modal>
  );
};

export default DiagnosticGenerateBudgetModal;
